import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  IconButton,
  Tooltip,
  useMediaQuery,
  Grid,
  Box,
  Tab,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Chip,
} from '@mui/material'
import RemoveRedEyeRoundedIcon from '@mui/icons-material/RemoveRedEyeRounded'
import CloseIcon from '@mui/icons-material/Close'
import LinkIcon from '@mui/icons-material/Link'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { useTheme } from '@mui/system'
import { brandColors, gradientColors, textColors } from '../../../Common/Color/Color'
import { fontSizes } from '../../../Common/Typography/TypographyStyles'
import { Task } from '../../../../redux/task/types'
import TaskBadge from '../TaskTableComponents/TaskBadge'
import { setDateFormat, setTimeFormat } from '../../../../utils/dateUtils'
import { useFetchImageUrl } from '../../../../hooks/componentHooks/useFetchImageUrl'

interface TaskViewModalProps {
  task: Task
}

const TaskViewModal: React.FC<TaskViewModalProps> = ({ task }) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('1')
  const [imageEnlarged, setImageEnlarged] = useState(false)

  // Fetch the task image using the proper hook
  const { imageUrl } = useFetchImageUrl(
    'images',
    task.imageFilename || null,
    'task',
    Boolean(task.imageFilename && task.imageFilename !== 'DefaultTaskImage')
  )

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue)
  }

  const handleImageClick = () => {
    if (imageUrl) {
      setImageEnlarged(true)
    }
  }

  const handleImageEnlargedClose = () => {
    setImageEnlarged(false)
  }


  const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return ''
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date
      if (isNaN(dateObj.getTime())) return ''
      return `${setDateFormat(dateObj.toISOString())} ${setTimeFormat(dateObj.toISOString())}`
    } catch (error) {
      console.error('Error formatting date:', error)
      return ''
    }
  }

  const getComplianceMetric = (): string => {
    return task.complianceMetric || ''
  }

  const getComplianceMetricColor = (): string => {
    const metric = getComplianceMetric()
    if (!metric) return '#000000'
    if (metric.includes('before')) return '#4caf50'  // green
    if (metric.includes('after')) return '#f44336'   // red
    return '#ff9800'                                  // orange (on due date)
  }

  const CustomizeTabPanel: React.FC<{ value: string; children: React.ReactNode }> = ({ value, children }) => {
    return (
      <TabPanel value={value} sx={{ flex: 1, p: 2 }}>
        <Box
          sx={{
            height: '100%',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#5f2b7f',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#555',
            },
            scrollbarWidth: 'auto',
            msOverflowStyle: 'auto',
          }}
        >
          {children}
        </Box>
      </TabPanel>
    )
  }

  return (
    <React.Fragment>
      <Tooltip title='View' arrow>
        <IconButton 
          color='success' 
          size='small' 
          onClick={handleOpen}
          data-testid={`task-view-button-${task.id}`}
        >
          <RemoveRedEyeRoundedIcon />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        aria-labelledby='task-dialog-title'
        fullScreen={fullScreen}
        sx={{
          '& .MuiDialog-paper': {
            width: fullScreen ? '100%' : '500px',
            maxWidth: fullScreen ? '100%' : '500px',
            height: fullScreen ? '100%' : 'auto',
            maxHeight: fullScreen ? '100%' : '80vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Sticky Header */}
        <Box 
          sx={{ 
            background: gradientColors.greenGradient, 
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', padding: '16px 48px 16px 24px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <DialogTitle
                sx={{
                  color: textColors.white,
                  fontWeight: 'bold',
                  fontSize: fontSizes.size20,
                  padding: 0,
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-all',
                  whiteSpace: 'normal',
                  flex: 1,
                }}
              >
                {task.title}
              </DialogTitle>
              <TaskBadge value={task.importance || 'low'} badgeType='importanceBadge' />
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              color: textColors.white,
            }}
            size='small'
            data-testid={`task-view-close-button-${task.id}`}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* Sticky Tab Navigation */}
        <Box 
          sx={{ 
            position: 'sticky',
            top: 0,
            zIndex: 999,
            backgroundColor: 'white',
            borderBottom: 1, 
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <TabContext value={value}>
            <TabList onChange={handleChange} aria-label='Task tabs' centered>
              <Tab label='Overview' value='1' />
              <Tab label='Timelines' value='2' />
              {task.imageFilename && task.imageFilename !== 'DefaultTaskImage' && <Tab label='Attachment' value='3' />}
            </TabList>
          </TabContext>
        </Box>
        
        {/* Scrollable Content Area */}
        <Box 
          sx={{ 
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TabContext value={value}>
            <CustomizeTabPanel value='1'>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box>
                  <Grid container spacing={2} direction='column' sx={{ marginTop: '16px' }}>
                    {/* Due Date and Documentation Chip Row */}
                    <Grid>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', marginBottom: '8px' }}>
                        {/* Due Date */}
                        <Box sx={{ flex: 1 }}>
                          <FormControl variant='outlined' fullWidth>
                            <InputLabel 
                              htmlFor='due-date-input-field-task-tab'
                              sx={{ 
                                backgroundColor: 'white',
                                paddingX: '4px',
                                fontSize: '0.875rem',
                                fontWeight: 500
                              }}
                            >
                              DUE DATE
                            </InputLabel>
                            <OutlinedInput
                              id='due-date-input-field-task-tab'
                              type='text'
                              value={formatDate(task.dueDate)}
                              readOnly
                              label='DUE DATE'
                              sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'rgba(0, 0, 0, 0.23)',
                                },
                              }}
                            />
                          </FormControl>
                        </Box>
                        
                        {/* Documentation Required Chip */}
                        <Box sx={{ 
                          flex: 1, 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'flex-end',
                          height: '56px', // Match the height of the input field
                          marginBottom: '15px' // Move chip up by 15px
                        }}>
                          {task.required ? (
                            <Chip
                              label="Documentation Required"
                              size="small"
                              sx={{
                                backgroundColor: '#e12339',
                                color: 'white',
                                fontWeight: 'bold',
                                '& .MuiChip-label': {
                                  fontSize: '0.75rem',
                                },
                              }}
                            />
                          ) : (
                            <Chip
                              label="Documentation Optional"
                              size="small"
                              sx={{
                                backgroundColor: '#3366cc',
                                color: 'white',
                                fontWeight: 'bold',
                                '& .MuiChip-label': {
                                  fontSize: '0.75rem',
                                },
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Grid>

                    <Grid container spacing={2} direction='column'>
                      {/* Task Description */}
                      <Box display='flex' alignItems='center' sx={{ marginBottom: '8px' }}>
                        <FormControl variant='outlined' fullWidth>
                          <InputLabel 
                            htmlFor='task-description-input-field'
                            sx={{ 
                              backgroundColor: 'white',
                              paddingX: '4px',
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}
                          >
                            TASK DESCRIPTION
                          </InputLabel>
                          <OutlinedInput
                            id='task-description-input-field'
                            type='text'
                            value={task.details || ''}
                            readOnly
                            label='TASK DESCRIPTION'
                            multiline
                            rows={4}
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                              },
                            }}
                          />
                        </FormControl>
                      </Box>

                      {/* Task Link */}
                      <Box display='flex' alignItems='center'>
                        <FormControl variant='outlined' fullWidth>
                          <InputLabel 
                            htmlFor='link-input-field'
                            sx={{ 
                              backgroundColor: 'white',
                              paddingX: '4px',
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}
                          >
                            TASK LINK
                          </InputLabel>
                          <OutlinedInput
                            id='link-input-field'
                            type='text'
                            value={task.link || ''}
                            readOnly
                            label='TASK LINK'
                            onClick={() => task.link && task.link.trim() !== '' && window.open(task.link, '_blank', 'noopener,noreferrer')}
                            sx={{
                              cursor: task.link && task.link.trim() !== '' ? 'pointer' : 'default',
                              '&:hover': {
                                backgroundColor: task.link && task.link.trim() !== '' ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                              },
                              '& .MuiOutlinedInput-input': {
                                cursor: task.link && task.link.trim() !== '' ? 'pointer' : 'default',
                                color: task.link && task.link.trim() !== '' ? brandColors.purpleTelus : 'inherit',
                                textDecoration: task.link && task.link.trim() !== '' ? 'underline' : 'none',
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                              },
                            }}
                            startAdornment={
                              <InputAdornment position='start'>
                                <LinkIcon />
                              </InputAdornment>
                            }
                          />
                        </FormControl>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </CustomizeTabPanel>
            <CustomizeTabPanel value='2'>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box>
                  <Grid container spacing={2} direction='column' sx={{ marginTop: '16px' }}>
                    <Grid container spacing={2} direction='column'>
                      {/* Assigned Date */}
                      <Box display='flex' alignItems='center' sx={{ marginBottom: '8px' }}>
                        <FormControl variant='outlined' fullWidth>
                          <InputLabel 
                            htmlFor='assigned-date-input-field'
                            sx={{ 
                              backgroundColor: 'white',
                              paddingX: '4px',
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}
                          >
                            ASSIGNED DATE
                          </InputLabel>
                          <OutlinedInput
                            id='assigned-date-input-field'
                            type='text'
                            value={formatDate(task.assignedDate)}
                            readOnly
                            label='ASSIGNED DATE'
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                              },
                            }}
                          />
                        </FormControl>
                      </Box>

                      {/* Started Date */}
                      <Box display='flex' alignItems='center' sx={{ marginBottom: '8px' }}>
                        <FormControl variant='outlined' fullWidth>
                          <InputLabel 
                            htmlFor='started-date-input-field'
                            sx={{ 
                              backgroundColor: 'white',
                              paddingX: '4px',
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}
                          >
                            STARTED DATE
                          </InputLabel>
                          <OutlinedInput
                            id='started-date-input-field'
                            type='text'
                            value={formatDate(task.startedDate)}
                            readOnly
                            label='STARTED DATE'
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                              },
                            }}
                          />
                        </FormControl>
                      </Box>

                      {/* Completion Date */}
                      <Box display='flex' alignItems='center' sx={{ marginBottom: '8px' }}>
                        <FormControl variant='outlined' fullWidth>
                          <InputLabel 
                            htmlFor='completion-date-input-field'
                            sx={{ 
                              backgroundColor: 'white',
                              paddingX: '4px',
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}
                          >
                            COMPLETION DATE
                          </InputLabel>
                          <OutlinedInput
                            id='completion-date-input-field'
                            type='text'
                            value={formatDate(task.completionDate)}
                            readOnly
                            label='COMPLETION DATE'
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                              },
                            }}
                          />
                        </FormControl>
                      </Box>

                      {/* Compliance Metric */}
                      {getComplianceMetric() && (
                        <Box display='flex' alignItems='center' sx={{ marginBottom: '8px' }}>
                          <FormControl variant='outlined' fullWidth>
                            <InputLabel 
                              htmlFor='compliance-metric-input-field'
                              sx={{ 
                                backgroundColor: 'white',
                                paddingX: '4px',
                                fontSize: '0.875rem',
                                fontWeight: 500
                              }}
                            >
                              COMPLIANCE METRIC
                            </InputLabel>
                            <OutlinedInput
                              id='compliance-metric-input-field'
                              type='text'
                              value={getComplianceMetric()}
                              readOnly
                              label='COMPLIANCE METRIC'
                              sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'rgba(0, 0, 0, 0.23)',
                                },
                                '& .MuiOutlinedInput-input': {
                                  color: getComplianceMetricColor(),
                                  fontWeight: 'bold',
                                },
                              }}
                            />
                          </FormControl>
                        </Box>
                      )}

                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </CustomizeTabPanel>
            {task.imageFilename && task.imageFilename !== 'DefaultTaskImage' && (
              <CustomizeTabPanel value='3'>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box>
                    <Grid container spacing={2} direction='column' sx={{ marginTop: '16px' }}>
                      <Grid container spacing={2} direction='column'>
                        {/* Documentation Image */}
                        <Box display='flex' flexDirection='column' alignItems='center' sx={{ marginBottom: '16px' }}>
                          {task.imageFilename && task.imageFilename !== 'DefaultTaskImage' ? (
                            <Box
                              sx={{
                                width: '100%',
                                maxWidth: '400px',
                                border: '2px solid #e0e0e0',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                backgroundColor: '#f5f5f5',
                              }}
                            >
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt="Task Documentation"
                                  onClick={handleImageClick}
                                  style={{
                                    width: '100%',
                                    height: 'auto',
                                    display: 'block',
                                    objectFit: 'contain',
                                    cursor: 'pointer',
                                  }}
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement
                                    target.style.display = 'none'
                                    const parent = target.parentElement
                                    if (parent) {
                                      parent.innerHTML = `
                                        <div style="
                                          display: flex;
                                          align-items: center;
                                          justify-content: center;
                                          height: 200px;
                                          color: #666;
                                          font-size: 14px;
                                          text-align: center;
                                          padding: 20px;
                                        ">
                                          <div>
                                            <div style="margin-bottom: 8px;">📷</div>
                                            <div>Image not available</div>
                                          </div>
                                        </div>
                                      `
                                    }
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '200px',
                                    color: '#666',
                                    fontSize: '14px',
                                    textAlign: 'center',
                                    padding: '20px',
                                  }}
                                >
                                  <Box>
                                    <Box sx={{ fontSize: '24px', marginBottom: '8px' }}>📷</Box>
                                    <Box>No documentation image uploaded</Box>
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                width: '100%',
                                maxWidth: '400px',
                                height: '200px',
                                border: '2px dashed #ccc',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#fafafa',
                                color: '#666',
                                fontSize: '14px',
                                textAlign: 'center',
                                padding: '20px',
                              }}
                            >
                              <Box>
                                <Box sx={{ fontSize: '24px', marginBottom: '8px' }}>📷</Box>
                                <Box>No documentation image uploaded</Box>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </CustomizeTabPanel>
            )}
          </TabContext>
        </Box>
      </Dialog>

      {/* Enlarged Image Modal */}
      <Dialog
        open={imageEnlarged}
        onClose={handleImageEnlargedClose}
        maxWidth={false}
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '90vh',
            minWidth: '90vw',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
          }}
          onClick={handleImageEnlargedClose}
        >
          <IconButton
            onClick={handleImageEnlargedClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              },
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
          {imageUrl && (
            <img
              src={imageUrl}
              alt="Enlarged Task Documentation"
              style={{
                maxWidth: '90%',
                maxHeight: '90%',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </Box>
      </Dialog>
    </React.Fragment>
  )
}

export default TaskViewModal
